import React, { useState, useEffect, useRef, useCallback } from 'react';
import L from 'leaflet';
import { LuPlay, LuPause, LuGauge } from 'react-icons/lu';
import RightSidebar from '../../components/common/layout/RightSidebar';
import { equipamentsToGeoJSON, getEquipamentColorForType } from '../../utils/equipamentsGeoJSON';
import { addBasemap } from '../../utils/addBasemap';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import 'leaflet/dist/leaflet.css';
import './Operacoes2.css';
import './OperacoesOffline.css';

function alertRiskColor(risk) {
  const r = (risk || 'Médio').toString().toLowerCase();
  if (r.includes('baixo')) return '#22c55e';
  if (r.includes('alto')) return '#ef4444';
  return '#eab308';
}

function alertRiskPriority(risk) {
  const r = (risk || 'Médio').toString().toLowerCase();
  if (r.includes('alto')) return 3;
  if (r.includes('médio') || r.includes('medio')) return 2;
  if (r.includes('baixo')) return 1;
  return 2;
}

function getAlertsActiveAtTime(alertsList, moment) {
  if (!Array.isArray(alertsList) || !moment) return [];
  const ts = typeof moment === 'string' ? new Date(moment).getTime() : Number(moment);
  if (Number.isNaN(ts)) return [];
  return alertsList.filter((a) => {
    const start = a.date_start ? new Date(a.date_start).getTime() : null;
    if (!start) return false;
    return ts >= start && ts <= start + (a.period ?? 0) * 60 * 1000;
  });
}

function buildEquipamentAlertColorMap(alertsList) {
  const map = new Map();
  if (!Array.isArray(alertsList)) return map;
  for (const a of alertsList) {
    const color = alertRiskColor(a.risk);
    const priority = alertRiskPriority(a.risk);
    for (const id of (a.equipamentos ?? [])) {
      const existing = map.get(id);
      if (!existing || existing.priority < priority) map.set(id, { color, priority });
    }
  }
  return map;
}

function getEquipamentLayerStyle(feature, activeAlerts) {
  const type = feature?.properties?.type;
  const color = getEquipamentColorForType(type);
  const geomType = feature?.geometry?.type;
  const equipId = feature?.properties?.id != null ? Number(feature.properties.id) : null;
  const alertMap = buildEquipamentAlertColorMap(activeAlerts);
  const alertEntry = equipId != null ? alertMap.get(equipId) : null;

  if (geomType === 'Polygon' || geomType === 'MultiPolygon') {
    if (alertEntry) {
      return { color: alertEntry.color, fillColor: alertEntry.color, fillOpacity: 0.3, weight: 2 };
    }
    return { color, fillColor: 'transparent', fillOpacity: 0, weight: 2 };
  }
  return { color, weight: 3 };
}

L.Marker.prototype.options.icon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const SNAPSHOT_URL = '/sipremo_cenario1_2026-05-06.json';
const IMAGE_OPACITY = 0.2;

const OperacoesOffline = ({ sessao = null, onSignOut = null }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const imageOverlayRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [snapshotData, setSnapshotData] = useState(null);
  const [imagesList, setImagesList] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1000);
  const animationIntervalRef = useRef(null);
  const [rightSidebarOpen, setRightSidebarOpen] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [idioma, setIdioma] = useState('pt'); // 'pt' | 'en'
  const equipamentsLayerRef = useRef(null);

  // Carregar snapshot do arquivo público (sem autenticação)
  useEffect(() => {
    fetch(SNAPSHOT_URL)
      .then((r) => {
        if (!r.ok) throw new Error('Arquivo de demonstração não encontrado');
        return r.json();
      })
      .then((data) => {
        setSnapshotData(data);
        setImagesList(data.imagesList ?? []);
        setSelectedImageIndex(data.selectedImageIndex ?? 0);
        setLoading(false);
      })
      .catch((err) => {
        setLoadError(err.message);
        setLoading(false);
      });
  }, []);

  // Inicializar mapa (sem chamadas autenticadas)
  useEffect(() => {
    if (mapRef.current && !mapInstanceRef.current) {
      const map = L.map(mapRef.current, {
        center: [37.0194, -7.9322],
        zoom: 13,
        scrollWheelZoom: true,
      });

      addBasemap(map);

      mapInstanceRef.current = map;
      setMapReady(true);
    }

    return () => {
      if (imageOverlayRef.current && mapInstanceRef.current) {
        mapInstanceRef.current.removeLayer(imageOverlayRef.current);
        imageOverlayRef.current = null;
      }
      if (equipamentsLayerRef.current && mapInstanceRef.current) {
        mapInstanceRef.current.removeLayer(equipamentsLayerRef.current);
        equipamentsLayerRef.current = null;
      }
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // Adicionar imagem ao mapa quando o índice mudar
  const addImageToMap = useCallback((imageData) => {
    if (!mapInstanceRef.current || !imageData?.public_url || !imageData?.metadata?.leaflet_bounds) return;

    if (imageOverlayRef.current) {
      mapInstanceRef.current.removeLayer(imageOverlayRef.current);
      imageOverlayRef.current = null;
    }

    const bounds = imageData.metadata.leaflet_bounds;
    const overlay = L.imageOverlay(
      imageData.public_url,
      [[bounds[0][0], bounds[0][1]], [bounds[1][0], bounds[1][1]]],
      { opacity: IMAGE_OPACITY, interactive: false }
    );
    overlay.addTo(mapInstanceRef.current);
    imageOverlayRef.current = overlay;
  }, []);

  useEffect(() => {
    if (imagesList.length > 0 && selectedImageIndex >= 0 && selectedImageIndex < imagesList.length) {
      addImageToMap(imagesList[selectedImageIndex]);
    }
  }, [selectedImageIndex, imagesList, addImageToMap]);

  // Controle de animação
  useEffect(() => {
    if (isPlaying && imagesList.length > 0) {
      animationIntervalRef.current = setInterval(() => {
        setSelectedImageIndex((prev) => (prev - 1 + imagesList.length) % imagesList.length);
      }, playbackSpeed);
    } else {
      if (animationIntervalRef.current) {
        clearInterval(animationIntervalRef.current);
        animationIntervalRef.current = null;
      }
    }
    return () => {
      if (animationIntervalRef.current) {
        clearInterval(animationIntervalRef.current);
        animationIntervalRef.current = null;
      }
    };
  }, [isPlaying, imagesList.length, playbackSpeed]);

  // Renderizar camada de equipamentos quando mapa e snapshot estiverem prontos
  useEffect(() => {
    if (!mapReady || !mapInstanceRef.current || !snapshotData?.equipamentsList?.length) return;

    if (equipamentsLayerRef.current) {
      mapInstanceRef.current.removeLayer(equipamentsLayerRef.current);
      equipamentsLayerRef.current = null;
    }

    const filtered = snapshotData.equipamentsList.filter((e) => e.geometry);
    const geojson = equipamentsToGeoJSON(filtered);
    if (geojson.features.length === 0) return;

    const layer = L.geoJSON(geojson, {
      style: (feature) => getEquipamentLayerStyle(feature, []),
    });
    layer.addTo(mapInstanceRef.current);
    equipamentsLayerRef.current = layer;
  }, [mapReady, snapshotData]);

  // Atualizar estilo dos equipamentos conforme o momento selecionado na linha do tempo
  useEffect(() => {
    const layer = equipamentsLayerRef.current;
    if (!layer?.getLayers || !snapshotData) return;
    const selectedTime = imagesList.length > 0 ? (imagesList[selectedImageIndex]?.last_modified ?? null) : null;
    const activeAlerts = getAlertsActiveAtTime(snapshotData.alertsList ?? [], selectedTime);
    layer.getLayers().forEach((leafletLayer) => {
      if (leafletLayer.feature) {
        leafletLayer.setStyle(getEquipamentLayerStyle(leafletLayer.feature, activeAlerts));
      }
    });
  }, [selectedImageIndex, imagesList, snapshotData]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('pt-PT', {
      timeZone: 'UTC',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handlePlayPause = () => {
    if (!isPlaying && imagesList.length > 0) {
      setSelectedImageIndex(imagesList.length - 1);
    }
    setIsPlaying((p) => !p);
  };

  const handleImageSelect = (index) => {
    setSelectedImageIndex(index);
    setIsPlaying(false);
  };

  const alertsList = snapshotData?.alertsList ?? [];
  const flightsList = snapshotData?.flightsList ?? [];
  const cenarioAtivo = snapshotData?.cenarioAtivo ?? '1';
  const rightMargin = rightSidebarOpen ? 520 : 50;

  return (
    <div className="operacoes2-container">
      <div className="offline-demo-badge">
        <span className="offline-demo-dot" />
        {idioma === 'en' ? 'Demo · Scenario' : 'Demo · Cenário'} {cenarioAtivo}
      </div>

      {sessao && (
        <button
          type="button"
          className="session-signout-icon"
          onClick={onSignOut}
          title="Sign out"
          aria-label="Sign out"
        >
          <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <path d="M16 17l5-5-5-5" />
            <path d="M21 12H9" />
          </svg>
        </button>
      )}

      <main
        className="main-content"
        style={{ marginLeft: 0, marginRight: `${rightMargin}px`, marginBottom: '86px' }}
      >
        <div className="fullscreen-content-container">
          <div className="operacoes2-content">
            <div className="operacoes2-map-container">
              <div ref={mapRef} className="operacoes2-map" />
              {loading && (
                <div className="operacoes2-loading-overlay">
                  <div className="operacoes2-loading-spinner" />
                  <p className="operacoes2-loading-text">{idioma === 'en' ? 'Loading demo...' : 'Carregando demonstração...'}</p>
                </div>
              )}
              {loadError && (
                <div className="operacoes2-loading-overlay">
                  <p className="operacoes2-loading-text">{loadError}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <div className="offline-bottom-bar" style={{ right: `${rightMargin}px`, visibility: loading || loadError ? 'hidden' : 'visible' }}>
        <div className="offline-bottom-controls">
          <button
            type="button"
            className={`play-pause-button${isPlaying ? ' playing' : ''}`}
            onClick={handlePlayPause}
            disabled={imagesList.length === 0}
            aria-label={isPlaying ? (idioma === 'en' ? 'Pause' : 'Pausar') : (idioma === 'en' ? 'Play' : 'Reproduzir')}
          >
            {isPlaying ? <LuPause size={20} /> : <LuPlay size={20} />}
          </button>

          <div className="offline-ruler-wrapper">
            <div className="time-ruler-label-axis">{idioma === 'en' ? 'Time' : 'Tempo'}</div>
            {imagesList.length === 0 ? (
              <div className="time-ruler-empty">{idioma === 'en' ? 'No images available' : 'Sem imagens disponíveis'}</div>
            ) : (
              <div className="time-ruler" role="group" aria-label="Linha do tempo">
                {[...imagesList].reverse().map((image, reversedIdx) => {
                  const index = imagesList.length - 1 - reversedIdx;
                  return (
                    <button
                      key={image.key || image.filename || index}
                      type="button"
                      className={`time-ruler-segment${selectedImageIndex === index ? ' selected' : ''}`}
                      onClick={() => handleImageSelect(index)}
                      title={formatDate(image.last_modified)}
                    >
                      <span className="time-ruler-segment-bar" />
                    </button>
                  );
                })}
              </div>
            )}
            {imagesList.length > 0 && (
              <div className="time-ruler-selected-time">
                {idioma === 'en' ? 'Time' : 'Momento'}: {formatDate(imagesList[selectedImageIndex]?.last_modified)}
              </div>
            )}
          </div>

          <div className="offline-speed-control" title={`${idioma === 'en' ? 'Interval' : 'Intervalo'}: ${(playbackSpeed / 1000).toFixed(1)}s`}>
            <LuGauge className="offline-speed-label" size={16} aria-hidden="true" />
            <input
              type="range"
              min={200}
              max={3000}
              step={200}
              value={playbackSpeed}
              onChange={(e) => setPlaybackSpeed(Number(e.target.value))}
              className="speed-slider"
              style={{ width: '72px' }}
            />
          </div>
        </div>
      </div>

      <RightSidebar
        rightSidebarOpen={rightSidebarOpen}
        onToggle={setRightSidebarOpen}
        alertas={alertsList}
        voos={flightsList}
        loading={false}
        selectedTime={imagesList[selectedImageIndex]?.last_modified ?? null}
        idioma={idioma}
        onIdiomaChange={setIdioma}
      />
    </div>
  );
};

export default OperacoesOffline;
